import { Dialog, DialogActions, DialogContent, FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { PublicationType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";

///
interface IProps {
    publicationId: string;
    title: string;
    publicationType: PublicationType;
    isDraft?: boolean | null;
    isThread?: boolean | null;
    onClose: (result: boolean | null) => void;
}
export const DeletePublicationFactory = ({ publicationId, title, isDraft, isThread, publicationType, onClose }: IProps) => {

    const api = useCoreApi();
    const snackbar = useSnackbar();
    const { t } = useTranslation("knowledge");
    const { permissionExplorer } = useSession();

    const [permanently, setPermanently] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [lastError, setLastError] = useState<any>(null);

    const canMoveToTrash = publicationType != PublicationType.Answer && !isThread && !isDraft;
    const canAdminTrash = permissionExplorer.hasOne([CorePermissions.KnowledgeManageTrashCan, CorePermissions.KnowledgeAdmin]);

    const doDelete = async () => {
        setDeleting(true);
        try {
            var result = await api.v1.knowledgePublication.deletePublication({ publicationId, permanently });
            if (result.data) {
                if (canAdminTrash && !permanently) snackbar.enqueueSnackbar(t("Feed.Card.Actions.DeleteConfirmDialog.TrashedOutSuccessMessage"), { variant: "success" });
                else snackbar.enqueueSnackbar(t("Feed.Card.Actions.DeleteConfirmDialog.DeleteSuccessMessage"), { variant: "success" });
            }
            else snackbar.enqueueSnackbar(t("Feed.Card.Actions.DeleteConfirmDialog.DeleteErrorMessage"), { variant: "error" });

            onClose(result.data);
        }
        catch (e: any) {
            setLastError(e);
        }
        finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open maxWidth="sm" fullWidth>
            <MprcDialogTitleClose
                title={t("Feed.Card.Actions.DeleteConfirmDialog.Title")}
                subtitle={title}
                onClose={() => onClose(null)}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type="post" visible={deleting} />
                <MprcRequestErrorDialog error={lastError} onClose={() => onClose(null)} />

                <MprcContainer margin={canMoveToTrash && canAdminTrash ? "large" : "none"}>
                    <Typography variant="body2">
                        {canAdminTrash ?
                            (!canMoveToTrash
                                ?
                                <>
                                    <span>{t("Feed.Card.Actions.DeleteConfirmDialog.ConfirmPermanentlyMessageL1")}</span>
                                    <br />
                                    <span>{t("Feed.Card.Actions.DeleteConfirmDialog.ConfirmMessageL2")}</span>
                                </>
                                :
                                <>
                                    <span>{t("Feed.Card.Actions.DeleteConfirmDialog.ConfirmTrashMessageL1")}</span>
                                    <br />
                                    <span>{t("Feed.Card.Actions.DeleteConfirmDialog.ConfirmMessageL2")}</span>
                                </>
                            )
                            : t("Feed.Card.Actions.DeleteConfirmDialog.ConfirmMessageL2")
                        }
                    </Typography>
                </MprcContainer>
                {canMoveToTrash && canAdminTrash &&
                    <FormControlLabel
                        control={<Switch checked={permanently} onChange={(_, checked) => setPermanently(checked)} />}
                        label={<Typography variant="body2">{t("Feed.Card.Actions.DeleteConfirmDialog.PermanentlyLabel")}</Typography>}
                        disableTypography
                    />
                }
            </DialogContent>
            <DialogActions sx={{ alignContent: "center" }}>
                <Grid container justifyContent="center" alignItems="center" spacing={2} width="100%">
                    <Grid>
                        <MprcButton variant="contained" size="medium" color="cancel" onClick={() => onClose(null)}>{t("Feed.Card.Actions.DeleteConfirmDialog.NotButtonText")}</MprcButton>
                    </Grid>
                    <Grid>
                        <MprcButton variant="contained" size="medium" color="error" onClick={doDelete}>{t("Feed.Card.Actions.DeleteConfirmDialog.YesButtonText")}</MprcButton>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog >
    );
};
